import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { userStore } from '../../store';
import { Loader, showToast, LocationPicker, PhoneInputField } from '../../components/shared';
import { BASE_URL } from '../../database/apis';
import { ArrowLeft, User, Mail, Save, Camera } from 'lucide-react';
import { Camera as CapCamera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';

const ProfileScreen: React.FC = observer(() => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '',
    country: '',
    state: '',
    city: '',
    zipCode: '',
    streetAddress: '',
  });

  useEffect(() => {
    if (userStore.user) {
      setForm({
        name: userStore.user.name || '',
        email: userStore.user.email || '',
        phone: userStore.user.phone || '',
        countryCode: userStore.user.countryCode || '',
        country: userStore.user.country || '',
        state: userStore.user.state || '',
        city: userStore.user.city || '',
        zipCode: userStore.user.zipCode || '',
        streetAddress: userStore.user.streetAddress || '',
      });
    }
  }, []);

  const updateField = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // Read a File into a base64 data URL. Used only by the web fallback —
  // CapCamera returns base64 directly, so we don't need this on native.
  const fileToDataUrl = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });

  const handlePickPhoto = async () => {
    // PWA / browser path. Capacitor's Camera plugin requires a native
    // bridge and throws when called from a regular web page. We use a
    // hidden <input type="file" capture> instead — iOS Safari + Android
    // Chrome both surface the camera + gallery picker for this.
    if (!Capacitor.isNativePlatform()) {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.setAttribute('capture', 'user');
      input.onchange = async () => {
        const file = input.files?.[0];
        if (!file) return;
        try {
          const dataUrl = await fileToDataUrl(file);
          userStore.uploadProfilePhoto(dataUrl, (success, message) => {
            showToast(
              message || (success ? 'Photo updated!' : 'Photo upload failed'),
              success ? 'success' : 'error',
            );
          });
        } catch {
          showToast('Could not read the selected image', 'error');
        }
      };
      input.click();
      return;
    }

    try {
      const image = await CapCamera.getPhoto({
        quality: 80,
        allowEditing: true,
        resultType: CameraResultType.Base64,
        source: CameraSource.Prompt,
        width: 500,
        height: 500,
      });

      if (image.base64String) {
        const base64Data = `data:image/${image.format};base64,${image.base64String}`;
        userStore.uploadProfilePhoto(base64Data, (success, message) => {
          if (success) {
            showToast(message || 'Photo updated!', 'success');
          } else {
            showToast(message || 'Photo upload failed', 'error');
          }
        });
      }
    } catch (e: any) {
      if (e.message !== 'User cancelled photos app') {
        showToast('Could not access camera/gallery', 'error');
      }
    }
  };

  const handleSave = () => {
    if (!form.name.trim()) {
      showToast('Name is required', 'error');
      return;
    }
    setSaving(true);
    userStore.updateUser(
      {
        name: form.name.trim(),
        phone: form.phone.trim(),
        countryCode: form.countryCode,
        country: form.country.trim(),
        state: form.state.trim(),
        city: form.city.trim(),
        zipCode: form.zipCode.trim(),
        streetAddress: form.streetAddress.trim(),
      },
      (success, message) => {
        setSaving(false);
        if (success) {
          showToast('Profile updated successfully!', 'success');
          navigate(-1);
        } else {
          showToast(message || 'Update failed', 'error');
        }
      }
    );
  };

  const profileImageUrl = userStore.user?.photo
    ? userStore.user.photo.startsWith('http')
      ? userStore.user.photo
      : `${BASE_URL}${userStore.user.photo}`
    : '';

  return (
    <div className="h-full flex flex-col bg-background">
      <Loader visible={saving || userStore.photoUploading} message={userStore.photoUploading ? 'Uploading photo...' : 'Saving...'} />

      {/* Header */}
      <div className="safe-area-top bg-background border-b border-border">
        <div className="flex items-center px-4 py-3">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-muted transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-foreground" />
          </button>
          <h1 className="flex-1 text-center text-lg font-semibold text-foreground">Edit Profile</h1>
          <button
            onClick={handleSave}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-muted transition-colors"
          >
            <Save className="w-5 h-5 text-secondary" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-6 py-6 space-y-5">
          {/* Avatar with Photo Picker */}
          <div className="flex justify-center mb-2">
            <button onClick={handlePickPhoto} className="relative group">
              <Avatar className="w-24 h-24">
                {profileImageUrl ? (
                  <AvatarImage src={profileImageUrl} alt={form.name} />
                ) : null}
                <AvatarFallback className="text-2xl font-semibold bg-secondary/10 text-secondary">
                  {form.name?.charAt(0)?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="absolute bottom-0 right-0 w-8 h-8 bg-secondary rounded-full flex items-center justify-center border-2 border-background shadow-sm">
                <Camera className="w-3.5 h-3.5 text-secondary-foreground" />
              </div>
            </button>
          </div>

          {/* Personal Info */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground z-10" />
                <Input
                  value={form.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="Full Name"
                  className="pl-10 h-12"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground z-10" />
                <Input
                  value={form.email}
                  disabled
                  className="pl-10 h-12 opacity-50"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Phone Number</Label>
              <PhoneInputField
                value={form.phone}
                onChange={(phone, dialCode) => {
                  setForm((prev) => ({ ...prev, phone, countryCode: dialCode }));
                }}
              />
            </div>
          </div>

          {/* Address with Cascading Dropdowns */}
          <div className="space-y-2">
            <Label>Address</Label>
            <LocationPicker
              country={form.country}
              state={form.state}
              city={form.city}
              onCountryChange={(code, name) => updateField('country', name)}
              onStateChange={(code, name) => updateField('state', name)}
              onCityChange={(name) => updateField('city', name)}
            />
            <div className="grid grid-cols-2 gap-3 mt-2">
              <Input
                value={form.zipCode}
                onChange={(e) => updateField('zipCode', e.target.value)}
                placeholder="Zip Code"
                className="h-12"
              />
              <Input
                value={form.streetAddress}
                onChange={(e) => updateField('streetAddress', e.target.value)}
                placeholder="Street"
                className="h-12"
              />
            </div>
          </div>

          <Button
            onClick={handleSave}
            className="w-full h-12 text-base font-semibold gap-2 bg-secondary text-secondary-foreground hover:bg-secondary/90"
          >
            <Save className="w-4 h-4" />
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
});

export default ProfileScreen;
